<template>
  <div>
    <InventoryHeaderTab />

    <div class="page-header">
      <div>
        <h5 class="page-title">Stock Requisition</h5>
        <p class="page-sub">{{ issue.batch_code }}</p>
      </div>
      <button type="button" @click="$router.go(-1)" class="btn-outline">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-block">
      <div class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;" role="status"></div>
      <p class="mt-3 text-muted">Loading...</p>
    </div>

    <template v-else>

      <!-- Status Banner -->
      <div class="status-banner" :class="bannerClass(issue.status)">
        <font-awesome-icon :icon="bannerIcon(issue.status)" class="banner-icon" />
        <div>
          <p class="banner-title">{{ statusLabel(issue.status) }}</p>
          <p class="banner-sub" v-if="issue.status === 'approved'">
            Issued on {{ formatDate(issue.issued_at) }}
          </p>
          <p class="banner-sub" v-else-if="issue.status === 'pending'">
            This requisition is awaiting approval
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="banner-actions" v-if="issue.status === 'pending'">
          <button
            v-if="permission.approve_internal_issue == 1"
            class="btn-approve"
            :disabled="actionLoading"
            @click="performAction('approve')"
          >
            <span v-if="actionLoading === 'approve'" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-1" />
            Approve & Issue
          </button>
          <button
            v-if="permission.approve_internal_issue == 1"
            class="btn-reject"
            :disabled="actionLoading"
            @click="performAction('reject')"
          >
            <span v-if="actionLoading === 'reject'" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="times" class="me-1" />
            Reject
          </button>
          <button
            v-if="permission.cancel_internal_issue == 1"
            class="btn-cancel"
            :disabled="actionLoading"
            @click="performAction('cancel')"
          >
            <span v-if="actionLoading === 'cancel'" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="ban" class="me-1" />
            Cancel
          </button>
        </div>
      </div>

      <!-- Info Cards -->
      <div class="info-grid">

        <div class="info-card">
          <p class="info-card__label">Reference</p>
          <p class="info-card__value mono">{{ issue.batch_code }}</p>
        </div>

        <div class="info-card">
          <p class="info-card__label">Date</p>
          <p class="info-card__value">{{ issue.date }}</p>
        </div>

        <div class="info-card">
          <p class="info-card__label">Store</p>
          <p class="info-card__value">{{ issue.store?.store_name ?? '—' }}</p>
        </div>

        <div class="info-card">
          <p class="info-card__label">Department</p>
          <p class="info-card__value">{{ issue.department || '—' }}</p>
        </div>

        <div class="info-card info-card--wide">
          <p class="info-card__label">Purpose</p>
          <p class="info-card__value">{{ issue.purpose }}</p>
        </div>

        <div class="info-card info-card--wide" v-if="issue.notes">
          <p class="info-card__label">Notes</p>
          <p class="info-card__value">{{ issue.notes }}</p>
        </div>

      </div>

      <!-- Items Table -->
      <div class="data-card">
        <div class="section-header">
          <div class="section-icon section-icon--teal">
            <font-awesome-icon icon="boxes" />
          </div>
          <div>
            <p class="section-title">Items Requested</p>
            <p class="section-sub">{{ issue.issue_items?.length ?? 0 }} product(s)</p>
          </div>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Variant</th>
                <th class="col-num">Qty Requested</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in issue.issue_items" :key="item.id">
                <td class="col-num text-muted-cell">{{ idx + 1 }}</td>
                <td>
                  <p class="prod-name">{{ item.product?.product_name }}</p>
                </td>
                <td class="text-muted-cell">{{ item.variant?.variant_name ?? '—' }}</td>
                <td class="col-num">
                  <span class="qty-badge">{{ item.quantity }}</span>
                </td>
                <td class="text-muted-cell">{{ item.notes || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import InventoryHeaderTab from "./component/inventoryheadertab.vue";
import axios from "axios";
import { useToast } from "vue-toastification";
import { reusableMixin } from "../../../lib/reusableMixin";
import { useStore } from "vuex";

export default {
  components: { InventoryHeaderTab },
  mixins: [reusableMixin],

  data() {
    return {
      issue: {}, loading: true, actionLoading: null,
      toast: useToast(), store: useStore(),
    };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
  },

  created() { this.fetchIssue(); },

  methods: {
    async fetchIssue() {
      this.loading = true;
      try {
        const res = await axios.get(`/inventory/internal-issue/${this.$route.params.id}`);
        this.issue = res.data.data;
      } catch {
        this.toast.error('Failed to load requisition.');
      } finally {
        this.loading = false;
      }
    },

    async performAction(action) {
      this.actionLoading = action;
      try {
        const res = await axios.post(`/inventory/internal-issue/${action}/${this.issue.id}`);
        this.toast.success(res.data.message);
        this.issue = res.data.data ?? this.issue;
        await this.fetchIssue();
      } catch (err) {
        this.toast.error(err?.response?.data?.message ?? 'Action failed.');
      } finally {
        this.actionLoading = null;
      }
    },

    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    statusLabel(status) {
      return { pending: 'Pending Approval', approved: 'Issued', rejected: 'Rejected', cancelled: 'Cancelled' }[status] ?? status;
    },

    bannerClass(status) {
      return { pending: 'banner--warning', approved: 'banner--success', rejected: 'banner--danger', cancelled: 'banner--neutral' }[status] ?? '';
    },

    bannerIcon(status) {
      return { pending: 'clock', approved: 'check-circle', rejected: 'times-circle', cancelled: 'ban' }[status] ?? 'info-circle';
    },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; font-family:monospace; }

.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.mt-3 { margin-top:12px; }

/* Status Banner */
.status-banner { display:flex; align-items:center; gap:14px; border-radius:12px; padding:16px 20px; margin-bottom:20px; flex-wrap:wrap; }
.banner-icon   { font-size:22px; flex-shrink:0; }
.banner-title  { font-size:14px; font-weight:700; margin:0 0 2px; }
.banner-sub    { font-size:12.5px; margin:0; opacity:.8; }
.banner-actions { margin-left:auto; display:flex; gap:8px; flex-wrap:wrap; }

.banner--warning { background:#fff8e6; border:1px solid #fde68a; color:#92400e; }
.banner--success { background:#ecfdf5; border:1px solid #a7f3d0; color:#065f46; }
.banner--danger  { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; }
.banner--neutral { background:#f4f7fb; border:1px solid #e4eaf2; color:#4a5e78; }

.btn-approve { display:inline-flex; align-items:center; gap:5px; background:#059669; color:#fff; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:none; cursor:pointer; }
.btn-approve:hover { background:#047857; }
.btn-reject  { display:inline-flex; align-items:center; gap:5px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:none; cursor:pointer; }
.btn-reject:hover  { background:#b91c1c; }
.btn-cancel  { display:inline-flex; align-items:center; gap:5px; background:#fff; color:#4a5e78; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-cancel:hover  { background:#f4f7fb; }
button:disabled { opacity:.6; cursor:not-allowed; }

/* Info grid */
.info-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
.info-card { background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:16px 18px; }
.info-card--wide { grid-column:span 2; }
.info-card__label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin:0 0 5px; }
.info-card__value { font-size:14px; font-weight:600; color:#1a2b45; margin:0; }
.mono { font-family:monospace; }

/* Data Card */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
.section-header { display:flex; align-items:flex-start; gap:14px; margin-bottom:20px; }
.section-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.section-icon--teal { background:rgba(13,148,136,.1); color:#0d9488; }
.section-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.section-sub   { font-size:12.5px; color:#7a90b0; margin:0; }

.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
.col-num { width:100px; text-align:center; }
.text-muted-cell { color:#4a5e78; }
.prod-name { font-weight:600; color:#1a2b45; margin:0; }
.qty-badge { display:inline-block; background:#eef2ff; color:#3730a3; border:1px solid #c7d2fe; border-radius:20px; padding:3px 12px; font-size:13px; font-weight:700; }

/* Misc */
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.me-1 { margin-right:4px; }

@media(max-width:991px) { .info-grid { grid-template-columns:repeat(2,1fr); } .info-card--wide { grid-column:span 2; } }
@media(max-width:575px) { .info-grid { grid-template-columns:1fr; } .info-card--wide { grid-column:span 1; } }
</style>
